import { Injectable } from '@angular/core'
import {
  FiltersStateModel,
  HttpService,
  IExportFileResponse,
  INew_TableAction,
  INew_TableFilter,
  ITimePeriodWithLocationIds,
  IUrlFilterItem,
  New_AggregatorType,
  New_TableColumnType,
  SignUrlService,
  TableActionsService
} from '@lla-platform/core/core-data-access'
import { Observable } from 'rxjs'
import { ICallIntelligenceDashboardResponse } from '../interfaces/call-intelligence-dashboard.interface'
import { ICallIntelligenceLeadsResponse } from '../interfaces/call-intelligence-lead.interface'
import {
  ICallIntelligenceAnalysisVersionsResponse,
  ICallIntelligenceLeadAnalysisRequest,
  ICallIntelligenceLeadDetailsResponse,
  ICallIntelligenceReviewedResponse
} from '../interfaces/call-intelligence-details.interface'
import { CALL_INTELLIGENCE_API_URL } from './call-intelligence-api-routes'
import { DateService } from '@lla-platform/core/core-util'
import { cloneDeep } from 'lodash'
import { LeadReceivedTimeBucketRanges } from '../enums/lead-received-time'
import { ICallIntelligenceDrilldownResponse } from '../interfaces/call-intelligence-details.drilldown'
import {
  ICallTagItem,
  ICallTagsFilteredResponse,
  ICallTagsResponse,
  IRetagCallsRequest,
  IRetagCallsResponse,
  IUpsertCallTagRequest
} from '../interfaces/call-tag.interface'

@Injectable({
  providedIn: 'root'
})
export class CallIntelligenceService {
  constructor(
    private http: HttpService,
    private dateService: DateService,
    private tableActionsService: TableActionsService,
    private signUrlService: SignUrlService
  ) {}

  getCallIntelligenceDashboard(
    payload: ITimePeriodWithLocationIds
  ): Observable<ICallIntelligenceDashboardResponse> {
    const { from, to } = payload
    return this.http.post(CALL_INTELLIGENCE_API_URL.dashboard, {
      ...payload,
      from: this.dateService.momentToDateString(from),
      to: this.dateService.momentToDateString(to)
    })
  }

  getCallIntelligenceDrilldown(
    payload: ITimePeriodWithLocationIds
  ): Observable<ICallIntelligenceDrilldownResponse> {
    const { from, to } = payload
    return this.http.post(CALL_INTELLIGENCE_API_URL.drilldown, {
      ...payload,
      from: this.dateService.momentToDateString(from),
      to: this.dateService.momentToDateString(to)
    })
  }

  exportCallIntelligenceDrilldown(payload: ITimePeriodWithLocationIds): Observable<IExportFileResponse> {
    const { from, to } = payload
    return this.http.post(CALL_INTELLIGENCE_API_URL.drilldownExport, {
      ...payload,
      from: this.dateService.momentToDateString(from),
      to: this.dateService.momentToDateString(to)
    })
  }

  getLeadsList(info: INew_TableAction): Observable<ICallIntelligenceLeadsResponse> {
    const { tableAction, tagIds } = this.prepareLeadsListRequest(info)
    return this.http.post(CALL_INTELLIGENCE_API_URL.leadsList, {
      ...this.tableActionsService.createRequestPayload(tableAction),
      tagIds
    })
  }

  exportLeadsList(info: INew_TableAction, includeCostColumns = false): Observable<IExportFileResponse> {
    const { tableAction, tagIds } = this.prepareLeadsListRequest(info)
    return this.http.post(CALL_INTELLIGENCE_API_URL.exportLeadsList, {
      ...this.tableActionsService.createRequestPayload(tableAction),
      tagIds,
      includeCostColumns
    })
  }

  getLeadDetails(id: string): Observable<ICallIntelligenceLeadDetailsResponse> {
    return this.http.get(CALL_INTELLIGENCE_API_URL.leadDetails(id))
  }

  linkContact(leadId: string, contactId: string): Observable<void> {
    return this.http.post(CALL_INTELLIGENCE_API_URL.linkContact(leadId, contactId), {})
  }

  unlinkContact(leadId: string): Observable<void> {
    return this.http.post(CALL_INTELLIGENCE_API_URL.unlinkContact(leadId), {})
  }

  markLeadReviewed(id: string): Observable<ICallIntelligenceReviewedResponse> {
    return this.http.post(CALL_INTELLIGENCE_API_URL.markReviewed(id), {})
  }

  searchContacts(body: Record<string, unknown>): Observable<{ contacts: any[]; count: number }> {
    return this.http.post(CALL_INTELLIGENCE_API_URL.searchContacts, body)
  }

  reProcessLead(id: string, message: string): Observable<ICallIntelligenceLeadDetailsResponse> {
    return this.http.post(CALL_INTELLIGENCE_API_URL.reProcessLead(id), { message })
  }

  editLeadAnalysis(
    id: string,
    request: ICallIntelligenceLeadAnalysisRequest
  ): Observable<ICallIntelligenceLeadDetailsResponse> {
    return this.http.put(CALL_INTELLIGENCE_API_URL.updateLeadsAnalysis(id), request)
  }

  getLeadAnalysisVersions(id: string): Observable<ICallIntelligenceAnalysisVersionsResponse> {
    return this.http.get(CALL_INTELLIGENCE_API_URL.getLeadsAnalysisVersions(id))
  }

  getActiveCallTags(): Observable<ICallTagsResponse> {
    return this.http.get(CALL_INTELLIGENCE_API_URL.activeTags)
  }

  getCallTags(): Observable<ICallTagsResponse> {
    return this.http.get(CALL_INTELLIGENCE_API_URL.callTags)
  }

  getFilteredCallTags(info: INew_TableAction): Observable<ICallTagsFilteredResponse> {
    return this.http.post(
      CALL_INTELLIGENCE_API_URL.callTagsFilters,
      this.tableActionsService.createRequestPayload(info)
    )
  }

  createCallTag(payload: IUpsertCallTagRequest): Observable<ICallTagItem> {
    return this.http.post(CALL_INTELLIGENCE_API_URL.callTags, payload)
  }

  updateCallTag(id: string, payload: IUpsertCallTagRequest): Observable<ICallTagItem> {
    return this.http.put(CALL_INTELLIGENCE_API_URL.callTag(id), payload)
  }

  deleteCallTag(id: string): Observable<void> {
    return this.http.delete(CALL_INTELLIGENCE_API_URL.callTag(id))
  }

  retagCalls(payload: IRetagCallsRequest): Observable<IRetagCallsResponse> {
    return this.http.post(CALL_INTELLIGENCE_API_URL.retagCalls, {
      ...payload,
      from: this.dateService.momentToDateString(payload.from),
      to: this.dateService.momentToDateString(payload.to)
    })
  }

  getQualityScoreClass(score?: number): 'no-score' | 'error' | 'warning' | 'success' {
    if (score === undefined) return 'no-score'
    if (score < 4) return 'error'
    if (score < 7) return 'warning'
    return 'success'
  }

  buildLeadsQueryParams(
    state: Partial<FiltersStateModel> | undefined,
    ...filters: INew_TableFilter[]
  ): IUrlFilterItem {
    const filterMap = filters.reduce<Record<string, INew_TableFilter>>(
      (acc, f) => ({ ...acc, [f.key]: f }),
      {}
    )
    return this.signUrlService.generateFilters({
      ...state,
      callIntelligenceLeadsListFilters: {
        ...state?.callIntelligenceLeadsListFilters,
        page: state?.callIntelligenceLeadsListFilters?.page ?? 1,
        filters: {
          ...state?.callIntelligenceLeadsListFilters?.filters,
          ...filterMap
        }
      }
    })
  }

  private prepareLeadsListRequest(info: INew_TableAction): {
    tableAction: INew_TableAction
    tagIds: string[]
  } {
    const newInfo = cloneDeep(info)
    const timeFilter = newInfo.filters?.['leadReceivedTime']
    if (timeFilter?.items?.[0] !== undefined) {
      const selectedBucket = `${timeFilter.items[0].value}`
      const rules = LeadReceivedTimeBucketRanges[selectedBucket] ?? []
      timeFilter.columnType = New_TableColumnType.number
      timeFilter.items = rules
      timeFilter.aggregator = New_AggregatorType.And
    }

    // Tags are a many-to-many join, so the selected tag ids are hoisted to a top-level `tagIds`
    // and the column filter is dropped — the backend resolves it with a subquery, not FilterService.
    // `tags` is a STRING_AGG aggregate on the backend projection, which SQL cannot ORDER BY.
    // Drop a stale persisted sort on it so restored filter state can't produce an untranslatable query.
    if (newInfo.sort?.active === 'tags') {
      newInfo.sort = { active: 'leadReceivedDateTime', direction: 'desc' }
    }

    // A checkbox filter carries every selected value in a single item's `value` array,
    // so the ids must be flattened out of each item rather than stringified per item.
    const tagFilter = newInfo.filters?.['tags']
    const tagIds: string[] = (tagFilter?.items ?? [])
      .flatMap((item) => (Array.isArray(item.value) ? item.value : [item.value]))
      .map((value) => `${value}`)
      .filter((value) => !!value && value !== 'null' && value !== 'undefined')
    if (tagFilter) {
      delete newInfo.filters?.['tags']
    }

    return { tableAction: newInfo, tagIds }
  }
}
